import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sidebar } from "@/components/layout/Sidebar";
import { Topbar } from "@/components/layout/Topbar";
import { MobileTabBar } from "@/components/layout/MobileTabBar";
import { MobileSidebarSheet } from "@/components/layout/MobileSidebarSheet";
import { OfflineBanner } from "@/components/layout/OfflineBanner";
import { PWAInstallPrompt } from "@/components/layout/PWAInstallPrompt";
import { ErrorBoundary } from "@/components/error/ErrorBoundary";
import { SidebarProvider } from "@/contexts/SidebarContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useCommandStore } from "@/stores/command";
import { useGlobalShortcuts } from "@/lib/shortcuts";
import { useShortcutsCheatsheet } from "@/components/shared";
import { useSidebarCollapsed } from "@/hooks/use-sidebar-collapsed";
import { useReducedMotion } from "@/hooks/use-reduced-motion";

export function AppShell() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { activeWorkspace: ws } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const setCmdOpen = useCommandStore((s) => s.setOpen);
  const reducedMotion = useReducedMotion();
  const { setOpen: setCheatsheetOpen } = useShortcutsCheatsheet();
  const { setCollapsed, toggle: toggleCollapsed } = useSidebarCollapsed();

  const base = `/workspaces/${workspaceId}`;

  useGlobalShortcuts({
    base,
    navigate,
    openCommand: () => setCmdOpen(true),
    openCheatsheet: () => setCheatsheetOpen(true),
    toggleSidebar: toggleCollapsed,
    expandSidebar: () => setCollapsed(false),
  });

  if (!ws) return <Navigate to="/workspaces" replace />;

  return (
    <SidebarProvider>
      {/* Skip-Link für Tastatur-Nutzer */}
      <a
        href="#content"
        className="sr-only focus-visible:not-sr-only focus-visible:fixed focus-visible:top-2 focus-visible:left-2 focus-visible:z-[60] focus-visible:rounded-md focus-visible:bg-bg-surface focus-visible:px-3 focus-visible:py-2 focus-visible:text-text-primary focus-visible:shadow-popover focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        Zum Inhalt springen
      </a>

      <div className="flex h-full bg-bg-canvas">
        {/* Desktop-Sidebar */}
        <nav
          aria-label="Hauptnavigation"
          className="hidden h-full md:block"
        >
          <Sidebar />
        </nav>
        {/* Mobile-Sidebar-Sheet (vaul) */}
        <MobileSidebarSheet />

        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar />
          <main
            id="content"
            role="main"
            className="flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-20 pt-3 sm:px-4 md:px-5 md:pb-5 md:pt-4"
          >
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-border-default bg-bg-surface shadow-sm">
              <motion.div
                key={location.pathname}
                initial={reducedMotion ? false : { opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: reducedMotion ? 0 : 0.18,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="flex min-h-0 flex-1 flex-col"
              >
                {/*
                  Route-scoped ErrorBoundary: ein Page-Crash killt nur die
                  Route, nicht die ganze AppShell. key={pathname} resettet
                  den Fehler bei Navigation — sonst würde der User in der
                  Recovery-UI feststecken, auch wenn er weiternavigiert.
                */}
                <ErrorBoundary scope="route" key={location.pathname}>
                  <Outlet />
                </ErrorBoundary>
              </motion.div>
            </div>
          </main>
        </div>

        {/* Mobile Bottom-Tab-Bar — nur < md */}
        <MobileTabBar />

        {/* Slim Offline-Banner top, dismissible (Spec §7 / §8) */}
        <OfflineBanner />

        {/* PWA-Install-Prompt — erscheint nach 2 Sessions (Spec §8) */}
        <PWAInstallPrompt />
      </div>
    </SidebarProvider>
  );
}
